import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PersonalInfoForm from "./PersonalInfoForm";
import PaymentForm from "./PaymentForm";
import Confirmation from "./Confirmation";

export default function PaymentStepper() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // Prendo i parametri dalla URL
  const domainNameParam = params.get("domain");
  const domainPriceParam = Number(params.get("price"));
  const yearsExtraParam = Number(params.get("years")) || 1;
  const domainExtensionParam = params.get("extension") || "com";
  const domainIdParam = Number(params.get("id")) || null;

  // Stato domain e yearsExtra, con inizializzazione da param URL
  const [domain, setDomain] = useState(() => {
    if (!domainNameParam) return null;
    return {
      id_domain: domainIdParam,
      name: domainNameParam,
      price: domainPriceParam,
      extension: domainExtensionParam,
    };
  });

  const [yearsExtra, setYearsExtra] = useState(yearsExtraParam);

  // Altri stati
  const [step, setStep] = useState(1);
  const [userInfo, setUserInfo] = useState({});
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  // Carico chiave Stripe e Stripe.js
  useEffect(() => {
    async function loadKey() {
      try {
        const host = import.meta.env.VITE_HOST;
        const port = import.meta.env.VITE_PORT;
        const token = localStorage.getItem("jwt_token");

        if (!token) {
          window.location.href = "/login";
          return;
        }

        const res = await fetch(`${host}:${port}/api/v1/orders/publishable-key`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Errore nel recupero della chiave Stripe");

        const data = await res.json();
        const stripeInstance = await loadStripe(data.publishable_key);
        setStripePromise(stripeInstance);
      } catch (error) {
        console.error("Errore nel caricamento di Stripe:", error.message || error);
      }
    }

    loadKey();
  }, []);

  // All'arrivo nella pagina: 
  // se manca domain dai param URL lo carico da localStorage
  // se trovo nel URL redirect_status = succeeded e payment_intent passo subito allo step 3
  useEffect(() => {
        
    if (!domain) {
      const savedDomain = localStorage.getItem("checkout_domain");
      if (savedDomain) {
        setDomain(JSON.parse(savedDomain));
      }
    }
    if (!yearsExtra) {
      const savedYears = localStorage.getItem("checkout_yearsExtra");
      if (savedYears) setYearsExtra(Number(savedYears));
    }

    // Se sto tornando da Stripe con successo
    const redirectStatus = params.get("redirect_status");
    const paymentIntentFromURL = params.get("payment_intent");
    const paymentIntentClientSecret = params.get("payment_intent_client_secret");

    if (redirectStatus === "succeeded" && paymentIntentFromURL && paymentIntentClientSecret) {
      // Imposto clientSecret per il PaymentForm (anche se non serve se siamo a conferma)
      setClientSecret(paymentIntentClientSecret);
      setStep(3);
    }
  }, [domain, params]);

  // Funzione chiamata dal form info personali per iniziare pagamento
  const handleInitPayment = async (info) => {
    if (!domain || !domain.id_domain) {
      alert("Dominio o ID dominio non disponibile.");
      return;
    }

    // Calcolo importo finale (aggiunta anni extra)
    const finalAmount = domain.price + (yearsExtra - 1) * 5;

    const paymentData = {
      domain_id: domain.id_domain,
      amount: finalAmount,
      currency: "eur",
      email: info.email,
      name: info.name,
      surname: info.surname,
    };

    try {
      const host = import.meta.env.VITE_HOST;
      const port = import.meta.env.VITE_PORT;
      const token = localStorage.getItem("jwt_token");

      if (!token) {
        alert("Token JWT mancante, esegui login.");
        return;
      }

      // Salvo su localStorage per persist di dati al redirect
      localStorage.setItem("checkout_domain", JSON.stringify(domain));
      localStorage.setItem("checkout_yearsExtra", yearsExtra.toString());

      const response = await fetch(`${host}:${port}/api/v1/orders/init_payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(paymentData),
      });

      const data = await response.json();

      if (response.ok && data.payment_intent) {
        setUserInfo(info);
        setClientSecret(data.payment_intent);
        setCustomerId(data.customer_id);
        setStep(2);
      } else {
        alert("Errore inizializzazione pagamento: " + (data.detail || "Sconosciuto"));
      }
    } catch (err) {
      console.error("Errore di connessione al server:", err);
      alert("Errore di connessione al server.");
    }
  };

  // Controllo dominio valido per mostrare o meno errore
  if (!domain || !domain.name || isNaN(domain.price)) {
    return (
      <div className="text-center text-red-600 font-semibold mt-10">
        Dominio non disponibile o dati mancanti. Ritorna alla pagina precedente.
      </div>
    );
  }

  // Calcolo prezzo totale con anni extra
  const prezzoBase = Number(domain.price);
  const anniExtra = Number(yearsExtra - 1) > 0 ? Number(yearsExtra - 1) : 0;
  const totalPrice = (prezzoBase + anniExtra * (prezzoBase * 0.7)).toFixed(2);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header verde */}
      <header className="relative py-10 px-4" style={{ backgroundColor: "#94c41c" }}>
        <div className="w-[80%] max-w-6xl mx-auto">
          <h1 className="text-3xl lg:text-4xl font-bold tracking-wide leading-tight text-white">
            Vuoi comprare il dominio <br />
            <span className="font-extrabold">
              {domain.name}.{domain.extension}
            </span>
            ?
          </h1>
        </div>
      </header>

      {/* Barra step fuori dalla sezione verde */}
      <div className="bg-white py-8">
        <div className="w-[80%] max-w-6xl mx-auto relative">
          {/* Linea stilizzata */}
          <div className="absolute top-5 left-10 right-10 h-[2px] bg-gray-300 z-0" />
          <div className="flex justify-between relative z-10">
            {["Info personali", "Pagamento", "Conferma"].map((label, idx) => {
              const number = idx + 1;
              const isActive = step === number;
              const isDone = step > number;

              return (
                <div key={label} className="flex flex-col items-center w-1/3">
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-md font-bold shadow-md transition-colors duration-300 ${
                      isDone
                        ? "bg-[#94c41c] text-white"
                        : isActive
                        ? "bg-white border-2 border-[#94c41c] text-[#94c41c]"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {isDone ? "✓" : number}
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contenuto principale */}
      <main className="flex-grow bg-gray-100 py-10 px-4">
        <div className="w-[78%] max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {/* Form area */}
          <div className="md:col-span-2 bg-white shadow-lg p-6 rounded-xl">
            {step === 1 && <PersonalInfoForm onNext={handleInitPayment} />}
            {step === 2 && clientSecret && stripePromise && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm onNext={() => setStep(3)} customerId={customerId} />
              </Elements>
            )}
            {step === 2 && !stripePromise && <div>Caricamento Stripe...</div>}
            {step === 3 && (
              <Confirmation userInfo={userInfo} domain={domain} yearsExtra={yearsExtra} />
            )}
          </div>

          {/* Riepilogo */}
          <div className="bg-white shadow-lg p-6 rounded-xl">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold">Riepilogo Ordine</h4>
              <a href="/" className="text-sm text-[#94c41c] hover:underline">
                Annulla e torna al sito
              </a>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-gray-700">
                <span>Prezzo Dominio</span>
                <span>€{domain.price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Anni aggiuntivi</span>
                <span>
                  {yearsExtra - 1} × €{(domain.price * 0.7).toFixed(2)}
                </span>
              </div>
              <div className="border-t pt-2 flex justify-between font-semibold text-gray-900">
                <span>Totale</span>
                <span>€{totalPrice}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
